import { supabase } from "./supabaseClient";
import { LocalizedText, SurveyOption, SurveyQuestionDraft } from "../types";

// ── Types ────────────────────────────────────────────────────────────────────
export type ImportQuestionType = 'TEXT' | 'MULTIPLE_CHOICE' | 'CHECKBOX' | 'RATING' | 'DATE';

// One spreadsheet column mapped to a Tinaney question (editable on the review screen).
export interface ImportColumn {
  index: number;                 // column index in the parsed file
  header: string;                // original header
  include: boolean;              // false = skip (timestamps, email, score, …)
  question_type: ImportQuestionType;
  question_text: LocalizedText;
  options: SurveyOption[];       // for MULTIPLE_CHOICE / CHECKBOX
  attribute_id?: string | null;  // optional vault link
}

export interface ImportBatchRow {
  row: number;                                       // original row index (stable id)
  answers: { questionId: string; value: any }[];
}

export const IMPORT_TYPES: ImportQuestionType[] = ['TEXT', 'MULTIPLE_CHOICE', 'CHECKBOX', 'RATING', 'DATE'];
const CHOICE_TYPES = ['MULTIPLE_CHOICE', 'CHECKBOX'];

// ── Pure helpers (mapping spreadsheet cells -> Tinaney answers) ───────────────
const norm = (s: any) => String(s ?? '').trim().toLowerCase();

// Split a multi-select cell ("A, B; C") into individual values.
export const splitMulti = (cell: any): string[] =>
  String(cell ?? '')
    .split(/[,;]+/)
    .map((s) => s.trim())
    .filter((s) => s !== '');

// Distinct non-empty values seen in a column across ALL rows (multi-split for choices).
export function distinctValues(rows: any[][], colIndex: number, multi: boolean): string[] {
  const seen = new Map<string, string>(); // normalized -> first original casing
  for (const r of rows) {
    const raw = r?.[colIndex];
    const parts = multi ? splitMulti(raw) : [String(raw ?? '').trim()];
    for (const p of parts) {
      if (p === '') continue;
      const k = norm(p);
      if (!seen.has(k)) seen.set(k, p);
    }
  }
  return Array.from(seen.values());
}

// Ensure a choice column's options cover EVERY value present in the file (no data loss),
// and fill rating min/max from observed numbers. Returns a question-ready draft column.
export function reconcileColumn(col: ImportColumn, rows: any[][]): ImportColumn {
  if (CHOICE_TYPES.includes(col.question_type)) {
    const multi = col.question_type === 'CHECKBOX';
    const have = new Map<string, SurveyOption>();
    (col.options || []).forEach((o) => have.set(norm(getLabel(o.label)), o));
    let n = have.size;
    for (const val of distinctValues(rows, col.index, multi)) {
      if (!have.has(norm(val))) {
        have.set(norm(val), { id: `opt-${col.index}-${++n}`, label: { en: val } });
      }
    }
    return { ...col, options: Array.from(have.values()) };
  }
  return { ...col };
}

const getLabel = (l: any): string => (l && (l.en || Object.values(l)[0])) || '';

// Convert one cell to the answer_value for its question (option ids for choices,
// number for ratings, string otherwise). Returns null when empty/unmatched.
export function cellToAnswerValue(col: ImportColumn, cell: any): any {
  const text = String(cell ?? '').trim();
  if (col.question_type === 'TEXT' || col.question_type === 'DATE') {
    return text === '' ? null : text;
  }
  if (col.question_type === 'RATING') {
    if (text === '') return null;
    const n = parseFloat(text.replace(/[^0-9.\-]/g, ''));
    return Number.isFinite(n) ? n : null;
  }
  // Build a label -> option id map once per call (small arrays).
  const byLabel = new Map<string, string>();
  (col.options || []).forEach((o) => byLabel.set(norm(getLabel(o.label)), o.id));
  if (col.question_type === 'MULTIPLE_CHOICE') {
    const id = byLabel.get(norm(text));
    return id || null;
  }
  // CHECKBOX -> array of option ids
  const ids = splitMulti(text).map((v) => byLabel.get(norm(v))).filter(Boolean);
  return ids.length ? ids : null;
}

// Build SurveyQuestionDraft[] (in column order) from the included, reconciled columns.
export function columnsToQuestions(columns: ImportColumn[], rows: any[][]): { questions: SurveyQuestionDraft[]; sourceColumns: ImportColumn[] } {
  const included = columns.filter((c) => c.include);
  const questions: SurveyQuestionDraft[] = [];
  const sourceColumns: ImportColumn[] = [];
  included.forEach((raw, i) => {
    const col = reconcileColumn(raw, rows);
    const config: any = { required: false };
    if (CHOICE_TYPES.includes(col.question_type)) config.options = col.options;
    if (col.question_type === 'RATING') {
      const nums = rows.map((r) => parseFloat(String(r?.[col.index] ?? '').replace(/[^0-9.\-]/g, ''))).filter((n) => Number.isFinite(n));
      config.min = nums.length ? Math.min(...nums) : 1;
      config.max = nums.length ? Math.max(...nums) : 5;
      config.step = 1;
    }
    questions.push({
      id: `imp-${col.index}-${i}`,
      order_index: i + 1,
      question_type: col.question_type,
      question_text: col.question_text && Object.keys(col.question_text).length ? col.question_text : { en: col.header },
      config,
      gemini_validation_prompt: '',
      attribute_id: col.attribute_id || null,
    });
    sourceColumns.push(col);
  });
  return { questions, sourceColumns };
}

// ── Edge-function / DB calls ─────────────────────────────────────────────────

// Ask the AI to infer a schema from headers + a SAMPLE of rows.
export async function inferImportSchema(headers: string[], sampleRows: any[][]): Promise<ImportColumn[]> {
  const { data, error } = await supabase.functions.invoke('infer-import-schema', {
    body: { headers, sampleRows: sampleRows.slice(0, 30) },
  });
  if (error) throw error;
  const cols = (data?.columns || []) as any[];
  // Normalize / guard the AI output into our shape; fall back to TEXT.
  return headers.map((header, index) => {
    const c = cols.find((x) => x.index === index) || {};
    const type: ImportQuestionType = IMPORT_TYPES.includes(c.question_type) ? c.question_type : 'TEXT';
    return {
      index,
      header,
      include: c.include !== false,
      question_type: type,
      question_text: c.question_text && typeof c.question_text === 'object' ? c.question_text : { en: header },
      options: Array.isArray(c.options) ? c.options.map((o: any, i: number) => ({ id: o.id || `opt-${index}-${i + 1}`, label: o.label || { en: String(o) } })) : [],
      attribute_id: c.attribute_id || null,
    } as ImportColumn;
  });
}

// Create the import batch record (RLS owner-only). Returns the batch id.
export async function createImportRecord(surveyId: string, filename: string, rowCount: number): Promise<string> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');
  const id = crypto.randomUUID();
  const { error } = await supabase.from('survey_imports').insert({
    id, survey_id: surveyId, created_by: user.id, filename, row_count: rowCount, status: 'PROCESSING',
  });
  if (error) throw error;
  return id;
}

export async function setImportStatus(importId: string, status: 'COMPLETED' | 'FAILED'): Promise<void> {
  await supabase.from('survey_imports').update({ status }).eq('id', importId);
}

// Insert one batch of responses (idempotent per row). Retries transient failures with backoff.
export async function importResponsesBatch(importId: string, surveyId: string, batch: ImportBatchRow[]): Promise<{ inserted: number; answers: number }> {
  let attempt = 0;
  for (;;) {
    const { data, error } = await supabase.functions.invoke('import-survey-responses', {
      body: { importId, surveyId, batch },
    });
    if (!error) return data;
    attempt++;
    if (attempt >= 4) throw error;
    await new Promise((r) => setTimeout(r, 1000 * 2 ** attempt)); // 2s,4s,8s
  }
}

// Embed one chunk of pending answers. Returns how many were embedded and how many remain.
export async function embedPendingAnswers(surveyId: string, limit = 100): Promise<{ embedded: number; remaining: number }> {
  const { data, error } = await supabase.functions.invoke('embed-pending-answers', {
    body: { surveyId, limit },
  });
  if (error) throw error;
  return data;
}
